import React from 'react';
import { ActivityIndicator, Modal, ScrollView, TextInput, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import type { useCancelSubscription } from '../hooks/useCancelSubscription';
import { COLORS } from '../profileStyles';
import type { Translate } from '../profileTypes';

interface CancelSubscriptionModalProps {
  cancelFlow: ReturnType<typeof useCancelSubscription>;
  isCardBackedTrial: boolean;
  t: Translate;
}

export function CancelSubscriptionModal({ cancelFlow, isCardBackedTrial, t }: CancelSubscriptionModalProps) {
  const {
    showCancelSubscription,
    closeCancelSubscription,
    isPaused,
    cancelStep,
    cancelReasonOptions,
    cancelReason,
    setCancelReason,
    cancelReasonNote,
    setCancelReasonNote,
    cancelLoading,
    offerQueue,
    offerIndex,
    offerI18nKey,
    cancelOptionsData,
    cancelOption,
    setCancelOption,
    handleCancelSurveyContinue,
    handleAcceptOffer,
    handleDeclineOffer,
    handleCancelSubscription,
  } = cancelFlow;

  return (
    <Modal visible={showCancelSubscription} animationType="slide" transparent>
      <View className="flex-1 bg-black/50 justify-center items-center p-4">
        <Card className="w-full max-w-sm" style={{ maxHeight: '90%' }}>
          <CardHeader className="pb-2">
            <Text className="text-xl font-semibold text-foreground text-center">
              {isCardBackedTrial
                ? t('profile.cancelTrial')
                : t('profile.cancelSubscription')}
            </Text>
          </CardHeader>
          <ScrollView showsVerticalScrollIndicator contentContainerStyle={{ paddingBottom: 4 }}>
          <CardContent>
            {cancelStep === 'survey' ? (
              <>
                <Text className="text-sm text-muted-foreground mb-4 text-center">
                  {t('profile.cancelReasonPrompt')}
                </Text>
                <View className="gap-2 mb-4">
                  {cancelReasonOptions.map((option) => {
                    const selected = cancelReason === option.id;
                    return (
                      <TouchableOpacity
                        key={option.id}
                        className={`p-3 rounded-xl border ${selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}
                        onPress={() => setCancelReason(option.id)}
                        accessibilityRole="radio"
                        accessibilityLabel={option.label}
                        accessibilityState={{ checked: selected, disabled: cancelLoading }}
                      >
                        <View className="flex-row items-center justify-between">
                          <Text className="text-sm font-medium text-foreground flex-1">{option.label}</Text>
                          <MaterialIcons
                            name={selected ? 'radio-button-checked' : 'radio-button-unchecked'}
                            size={22}
                            color={selected ? COLORS.primary : '#9CA3AF'}
                          />
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                <TextInput
                  value={cancelReasonNote}
                  onChangeText={(value) => setCancelReasonNote(value.slice(0, 300))}
                  placeholder={t('profile.cancelReasonNotePlaceholder')}
                  multiline
                  maxLength={300}
                  className="border border-gray-300 rounded-xl px-3 py-3 mb-4 min-h-[80px]"
                />
              </>
            ) : null}

            {cancelStep === 'offer' && offerQueue[offerIndex] ? (
              <View className="items-center gap-3 mb-6">
                <MaterialIcons name="redeem" size={42} color={COLORS.primary} />
                <Text className="text-xl font-semibold text-foreground text-center">
                  {t(`profile.retentionOffers.${offerI18nKey(offerQueue[offerIndex])}.title`)}
                </Text>
                <Text className="text-sm text-muted-foreground text-center">
                  {t(`profile.retentionOffers.${offerI18nKey(offerQueue[offerIndex])}.description`)}
                </Text>
              </View>
            ) : null}

            {cancelStep === 'confirm' ? (
              <>
                <Text className="text-sm text-muted-foreground mb-4 text-center">
                  {isCardBackedTrial
                    ? t('profile.cancelTrialMessage')
                    : isPaused
                      ? t('profile.cancelPausedMessage')
                      : t('profile.cancelSubscriptionMessage')}
                </Text>
                <View className="gap-3 mb-6">
                  {cancelOptionsData.map((option) => {
                    const selected = cancelOption === option.id;
                    return (
                      <TouchableOpacity
                        key={option.id}
                        className={`p-4 rounded-xl border ${selected ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
                        onPress={() => setCancelOption(option.id)}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: selected, disabled: cancelLoading }}
                      >
                        <View className="flex-row items-center justify-between">
                          <View className="flex-1 pr-3">
                            <Text className={`text-base font-semibold ${selected ? 'text-red-700' : 'text-foreground'}`}>
                              {option.title}
                            </Text>
                            <Text className="text-sm text-muted-foreground mt-1">{option.description}</Text>
                          </View>
                          <MaterialIcons
                            name={selected ? 'radio-button-checked' : 'radio-button-unchecked'}
                            size={22}
                            color={selected ? COLORS.error : '#9CA3AF'}
                          />
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </>
            ) : null}
            <View className="flex-row gap-3">
              <Button
                variant="secondary"
                size="default"
                onPress={() => closeCancelSubscription()}
                disabled={cancelLoading}
                className="flex-1"
              >
                <Text className="font-medium">{t('profile.cancel')}</Text>
              </Button>
              {cancelStep === 'survey' ? (
                <Button
                  size="default"
                  onPress={handleCancelSurveyContinue}
                  disabled={!cancelReason || cancelLoading}
                  className="flex-1"
                >
                  <Text className="text-white font-medium">{t('common.continue')}</Text>
                </Button>
              ) : null}
              {cancelStep === 'offer' && offerQueue[offerIndex] ? (
                <View className="flex-1 gap-2">
                  <Button size="default" onPress={handleAcceptOffer} disabled={cancelLoading}>
                    {cancelLoading ? <ActivityIndicator color="white" /> : (
                      <Text className="text-white font-medium">
                        {t(`profile.retentionOffers.${offerI18nKey(offerQueue[offerIndex])}.accept`)}
                      </Text>
                    )}
                  </Button>
                  <Button variant="ghost" size="default" onPress={handleDeclineOffer} disabled={cancelLoading}>
                    <Text className="text-sm text-muted-foreground">{t('profile.retentionOfferDecline')}</Text>
                  </Button>
                </View>
              ) : null}
              {cancelStep === 'confirm' ? (
                <Button
                  testID="cancel-subscription-confirm"
                  variant="destructive"
                  size="default"
                  onPress={handleCancelSubscription}
                  disabled={cancelLoading}
                  className="flex-1"
                >
                  {cancelLoading ? <ActivityIndicator color="white" /> : (
                    <Text className="text-white font-medium">
                      {isCardBackedTrial ? t('profile.cancelTrial') : t('profile.cancelSubscription')}
                    </Text>
                  )}
                </Button>
              ) : null}
            </View>
          </CardContent>
          </ScrollView>
        </Card>
      </View>
    </Modal>
  );
}
